import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BRAND_OPTIONS } from "../shared/brands";
import SearchSelect from "./components/SearchSelect";
import TermsModal from "./components/TermsModal";
import {
  getNrcTownships,
  isValidNrcDigits,
  NRC_STATES,
  regionOptions,
  sanitizeNrcDigits,
  toMyanmarDigits,
  townshipOptions
} from "./formData";
import { MAX_SOURCE_PHOTO_BYTES, preparePhoto } from "./photo";
import { createSubmissionKey } from "./submissionKey";
import dkshLogo from "./assets/dksh-logo.png";

type Campaign = {
  title: string;
  startsAt: string;
  closesAt: string;
  timezone: string;
  phase: "PRELAUNCH" | "TEST" | "UPCOMING" | "OPEN" | "CLOSED";
};

type SubmitState =
  | { kind: "idle" }
  | { kind: "sending"; message: string }
  | { kind: "error"; message: string }
  | { kind: "success"; entryNo: string; entryMode: "TEST" | "LIVE" };

type FormErrors = {
  brands?: string;
  consent?: string;
  location?: string;
  nrcDigits?: string;
};

const NRC_TYPES = [
  { value: "N", label: "နိုင်" },
  { value: "E", label: "ဧည့်" },
  { value: "P", label: "ပြု" }
] as const;
const WALLET_TYPES = ["KBZPay", "WavePay", "AYA Pay", "CB Pay", "Other"];
const CONSENT_VERSION = "2026-09-24-terms-v2";

function FieldError({ text, id }: { text?: string; id?: string }) {
  return text ? <span className="field-error" id={id} role="alert">{text}</span> : null;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function TestModeBanner() {
  return <div className="notice test-mode-banner" role="status"><strong>TEST MODE — Entries are not eligible for the prize draw</strong><br />စမ်းသပ်မုဒ် — ဤစာရင်းများသည် ကံစမ်းမဲဆုအတွက် အကျုံးမဝင်ပါ။</div>;
}

export default function App() {
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [campaignError, setCampaignError] = useState("");
  const [submitState, setSubmitState] = useState<SubmitState>({ kind: "idle" });
  const [errors, setErrors] = useState<FormErrors>({});
  const [nrcState, setNrcState] = useState("");
  const [nrcTownship, setNrcTownship] = useState("");
  const [nrcDigits, setNrcDigits] = useState("");
  const [cityCode, setCityCode] = useState("");
  const [townshipCode, setTownshipCode] = useState("");
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [photoInfo, setPhotoInfo] = useState<{ name: string; size: number } | null>(null);
  const [termsOpen, setTermsOpen] = useState(false);
  const termsButtonRef = useRef<HTMLButtonElement>(null);
  const turnstileRef = useRef<HTMLDivElement>(null);
  const turnstileToken = useRef("");
  const submissionKey = useRef(createSubmissionKey());
  const displayStandId = useMemo(
    () => (new URLSearchParams(window.location.search).get("stand") ?? "").trim().toUpperCase(),
    []
  );
  const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;
  const nrcTownships = useMemo(() => getNrcTownships(nrcState), [nrcState]);
  const cities = useMemo(() => regionOptions(), []);
  const townships = useMemo(() => townshipOptions(cityCode), [cityCode]);
  const closeTerms = useCallback(() => setTermsOpen(false), []);

  useEffect(() => {
    fetch("/api/campaign", { headers: { accept: "application/json" } })
      .then(async (response) => {
        if (!response.ok) throw new Error("CAMPAIGN_LOAD_FAILED");
        setCampaign((await response.json()) as Campaign);
      })
      .catch(() => setCampaignError("Campaign အချက်အလက်ကို မရယူနိုင်ပါ။ Internet ချိတ်ဆက်မှုကို စစ်ဆေးပြီး ပြန်ဖွင့်ပါ။"));
  }, []);

  useEffect(() => {
    if (!turnstileSiteKey) return;

    let widgetId: string | undefined;
    const renderTimer = window.setInterval(() => {
      if (!turnstileRef.current || !window.turnstile || widgetId) return;
      window.clearInterval(renderTimer);
      widgetId = window.turnstile.render(turnstileRef.current, {
        sitekey: turnstileSiteKey,
        "refresh-expired": "auto",
        callback: (token) => {
          turnstileToken.current = token;
        },
        "expired-callback": () => {
          turnstileToken.current = "";
        },
        "error-callback": () => setSubmitState({
          kind: "error",
          message: "Security check ကို ဖွင့်၍မရပါ။ Internet ချိတ်ဆက်မှုစစ်ပြီး page ကို refresh လုပ်ပါ။"
        })
      });
    }, 100);

    return () => {
      window.clearInterval(renderTimer);
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
    };
  }, [turnstileSiteKey]);

  function toggleBrand(code: string) {
    setSelectedBrands((current) => current.includes(code)
      ? current.filter((item) => item !== code)
      : [...current, code]);
    setErrors((current) => ({ ...current, brands: undefined }));
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitState.kind === "sending") return;
    const formElement = event.currentTarget;
    const nextErrors: FormErrors = {};
    if (!isValidNrcDigits(nrcDigits)) nextErrors.nrcDigits = "NRC နံပါတ် ၆ လုံး ပြည့်စုံစွာ ထည့်ပါ။";
    if (!cityCode || !townshipCode) nextErrors.location = "ပြည်နယ်/တိုင်းနှင့် မြို့နယ်ကို ရွေးပါ။";
    if (!selectedBrands.length) nextErrors.brands = "အနည်းဆုံး Brand တစ်ခု ရွေးပါ။";
    if (!consentAccepted) nextErrors.consent = "စည်းမျဉ်း စည်းကမ်းချက်များကို သဘောတူရန် လိုအပ်ပါသည်။";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length || !formElement.reportValidity()) return;
    if (turnstileSiteKey && !turnstileToken.current) {
      setSubmitState({ kind: "error", message: "Security check ကို အပြီးသတ်ပြီး ပြန်လည်ပေးပို့ပါ။" });
      return;
    }

    setSubmitState({ kind: "sending", message: "ပုံကို ပြင်ဆင်နေပါသည်…" });

    try {
      const form = new FormData(formElement);
      form.set("nrcNo", `${nrcState}/${nrcTownship}(${form.get("nrcType")})${nrcDigits}`);
      form.delete("nrcState");
      form.delete("nrcTownship");
      form.delete("nrcType");
      form.delete("nrcDigits");
      form.set("cityCode", cityCode);
      form.set("townshipCode", townshipCode);
      form.delete("brandCodes");
      selectedBrands.forEach((brandCode) => form.append("brandCodes", brandCode));
      form.set("consentAccepted", "true");
      form.set("consentVersion", CONSENT_VERSION);
      form.set("displayStandId", displayStandId);
      if (turnstileToken.current) form.set("turnstileToken", turnstileToken.current);

      const originalPhoto = form.get("photo");
      if (!(originalPhoto instanceof File) || !originalPhoto.size) throw new Error("PHOTO_REQUIRED");
      if (originalPhoto.size > MAX_SOURCE_PHOTO_BYTES) throw new Error("PHOTO_SOURCE_TOO_LARGE");
      const photo = await preparePhoto(originalPhoto);
      if (photo.size > 5 * 1024 * 1024) throw new Error("PHOTO_COMPRESSED_TOO_LARGE");
      form.set("photo", photo);

      setSubmitState({ kind: "sending", message: "ကံစမ်းမဲစာရင်း ပေးပို့နေပါသည်…" });
      const response = await fetch("/api/entries", {
        method: "POST",
        headers: { "idempotency-key": submissionKey.current },
        body: form
      });
      const result = (await response.json()) as { entryNo?: string; entryMode?: "TEST" | "LIVE"; error?: { code?: string } };
      if (!response.ok || !result.entryNo) throw new Error(result.error?.code ?? "SUBMISSION_FAILED");
      setSubmitState({ kind: "success", entryNo: result.entryNo, entryMode: result.entryMode === "TEST" ? "TEST" : "LIVE" });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      const code = error instanceof Error ? error.message : "SUBMISSION_FAILED";
      const messages: Record<string, string> = {
        PHOTO_REQUIRED: "DKSH အနီရောင် ပြသစင်တစ်ခုလုံးပါဝင်သည့်ပုံကို ရွေးပါ။",
        PHOTO_SOURCE_TOO_LARGE: "မူရင်းပုံအရွယ်အစားသည် 15 MB ထက် မကျော်ရပါ။",
        PHOTO_DECODE_FAILED: "ဤပုံကို ဖတ်၍မရပါ။ မှန်ကန်သော JPG သို့မဟုတ် WebP ပုံတစ်ပုံ ရွေးပါ။",
        PHOTO_CONVERSION_FAILED: "ပုံကို ပြင်ဆင်၍မရပါ။ အခြားပုံတစ်ပုံဖြင့် ပြန်စမ်းပါ။",
        PHOTO_COMPRESSED_TOO_LARGE: "ပုံကို 5 MB အောက်အဖြစ် လျှော့ချ၍မရပါ။ အခြားပုံတစ်ပုံဖြင့် ပြန်စမ်းပါ။",
        DUPLICATE_NRC: "ဤမှတ်ပုံတင်အမှတ်ဖြင့် ကံစမ်းမဲစာရင်းသွင်းပြီးဖြစ်ပါသည်။",
        CAMPAIGN_PRELAUNCH: "ကမ်ပိန်းကို မဖွင့်လှစ်သေးပါ။ Campaign is not open yet.",
        CAMPAIGN_NOT_OPEN: "ယခုအချိန်တွင် ကံစမ်းမဲစာရင်း လက်မခံသေးပါ။",
        INVALID_DISPLAY_STAND: "ဤ Display Stand QR Code သည် အကျုံးမဝင်ပါ။ မှန်ကန်သော QR Code ကို ပြန်လည် Scan ဖတ်ပါ။",
        INVALID_FIELDS: "ဖြည့်သွင်းထားသော အချက်အလက်များကို ပြန်လည်စစ်ဆေးပါ။",
        INVALID_BRANDS: "ခွင့်ပြုထားသော Brand များထဲမှ အနည်းဆုံးတစ်ခု ရွေးပါ။",
        CONSENT_REQUIRED: "စည်းမျဉ်း စည်းကမ်းချက်များကို သဘောတူရန် လိုအပ်ပါသည်။",
        INVALID_PHOTO_SIZE: "ပြင်ဆင်ပြီးပုံအရွယ်အစားသည် 5 MB ထက် မကျော်ရပါ။",
        INVALID_PHOTO_TYPE: "မှန်ကန်သော JPG သို့မဟုတ် WebP ပုံကို ရွေးပါ။",
        BOT_CHECK_REQUIRED: "Security check ကို အပြီးသတ်ပြီး ပြန်လည်ပေးပို့ပါ။",
        BOT_CHECK_FAILED: "Security check မအောင်မြင်ပါ။ Page ကို refresh လုပ်ပြီး ပြန်စမ်းပါ။",
        RATE_LIMITED: "အကြိမ်များစွာ ကြိုးစားထားပါသည်။ ၁ မိနစ်ခန့်စောင့်ပြီး ပြန်စမ်းပါ။",
        SUBMISSION_FAILED: "ပေးပို့မှု မအောင်မြင်ပါ။ Internet ချိတ်ဆက်မှုစစ်ပြီး ပြန်စမ်းပါ။"
      };
      setSubmitState({ kind: "error", message: messages[code] ?? "ပေးပို့မှု မအောင်မြင်ပါ။ ခဏအကြာ ပြန်စမ်းပါ။" });
    }
  }

  if (submitState.kind === "success") {
    return (
      <main className="page success-page">
        {submitState.entryMode === "TEST" && <TestModeBanner />}
        <section className="card success-card" aria-live="polite">
          <div className="success-icon">✓</div>
          <p className="eyebrow">စာရင်းသွင်းပြီးပါပြီ</p>
          <h1>ပါဝင်ပေးသည့်အတွက် ကျေးဇူးတင်ပါသည်</h1>
          <p>ကံစမ်းမဲရည်ညွှန်းနံပါတ်</p>
          <strong className="entry-no">{submitState.entryNo}</strong>
          {submitState.entryMode === "TEST" && <p><strong>TEST ENTRY — Prize draw အတွက် အကျုံးမဝင်ပါ။</strong></p>}
          <p className="muted">ဤနံပါတ်ကို screenshot ရိုက်၍ သိမ်းထားပါ။ ကံထူးရှင်ဆက်သွယ်ရာတွင် အသုံးပြုနိုင်ပါသည်။</p>
        </section>
      </main>
    );
  }

  const phase = campaign?.phase;
  const formDisabled = phase !== "OPEN" && phase !== "TEST";
  const isSending = submitState.kind === "sending";

  return (
    <main className="page">
      <header className="hero">
        <div className="brand-mark"><img className="brand-logo" src={dkshLogo} alt="DKSH" /></div>
        <p className="eyebrow">Myanmar GT</p>
        <h1>Scan &amp; Win Lucky Draw</h1>
        <p className="subtitle">ကံစမ်းမဲပါဝင်ရန် ဖောင်</p>
        <div className="campaign-dates"><span>1st November</span><span>to</span><span>31st January 2027</span></div>
      </header>

      <section className="card intro-card">
        <h2>စာရင်းမသွင်းမီ</h2>
        <ol>
          <li>အချက်အလက်များကို မှန်ကန်စွာ ဖြည့်ပါ။</li>
          <li>DKSH မှကုန်ပစ္စည်းများခင်းကျင်းပြသထားသော အနီရောင်စင် တစ်ခုလုံးပါအောင်ဓာတ်ပုံကို ရှင်းလင်းစွာရိုက်ကူးပြီးတင်ပေးပါ။</li>
          <li>NRC တစ်ခုလျှင် တစ်ကြိမ်သာ ပါဝင်နိုင်ပါသည်။</li>
          <li>DKSH နှင့် ပူးပေါင်းအမှတ်တံဆိပ် ဝန်ထမ်းများ ပါဝင်ခွင့်မရှိပါ။</li>
        </ol>
      </section>

      {campaignError && <div className="notice error-notice">{campaignError}</div>}
      {phase === "PRELAUNCH" && <div className="notice" role="status"><strong>ကမ်ပိန်းကို မဖွင့်လှစ်သေးပါ။</strong><br />Campaign is not open yet.</div>}
      {phase === "TEST" && <TestModeBanner />}
      {phase === "UPCOMING" && <div className="notice">ကံစမ်းမဲကို 1 October 2026 တွင် စတင်ပါမည်။</div>}
      {phase === "CLOSED" && <div className="notice">ကံစမ်းမဲပါဝင်ချိန် ပြီးဆုံးသွားပါပြီ။</div>}

      <form className="entry-form" onSubmit={onSubmit} noValidate>
        <section className="card form-section">
          <div className="section-heading"><span>1</span><div><h2>ပါဝင်သူ အချက်အလက်</h2><p>Participant Information</p></div></div>
          <label>အမည်အပြည့်အစုံ <small>Full Name</small><input name="fullName" autoComplete="name" maxLength={200} required placeholder="ဥပမာ — မောင်မောင်" /></label>

          <fieldset>
            <legend>မှတ်ပုံတင်အမှတ် <small>NRC Number</small></legend>
            <div className="nrc-grid">
              <label><span>ပြည်နယ် / တိုင်း</span><select name="nrcState" value={nrcState} required onChange={(event) => { setNrcState(event.target.value); setNrcTownship(""); }}><option value="" disabled>ရွေးပါ</option>{NRC_STATES.map((state) => <option value={state.code} key={state.code}>{toMyanmarDigits(state.code)}</option>)}</select></label>
              <label><span>မြို့နယ်ကုဒ်</span><select name="nrcTownship" value={nrcTownship} required disabled={!nrcState} onChange={(event) => setNrcTownship(event.target.value)}><option value="" disabled>မြို့နယ်ကုဒ် ရွေးပါ</option>{nrcTownships.map((township) => <option value={township.code} key={township.code}>{township.codeMm}</option>)}</select></label>
              <label><span>အမျိုးအစား</span><select name="nrcType" required>{NRC_TYPES.map((type) => <option value={type.value} key={type.value}>{type.label}</option>)}</select></label>
              <label><span>နံပါတ် ၆ လုံး</span><input name="nrcDigits" type="text" inputMode="numeric" pattern="[0-9]*" minLength={6} maxLength={6} autoComplete="off" required value={nrcDigits} aria-invalid={Boolean(errors.nrcDigits)} aria-describedby="nrc-digits-error" onChange={(event) => { setNrcDigits(sanitizeNrcDigits(event.target.value)); setErrors((current) => ({ ...current, nrcDigits: undefined })); }} placeholder="123456" /></label>
            </div>
            <FieldError id="nrc-digits-error" text={errors.nrcDigits} />
          </fieldset>

          <label>ဖုန်းနံပါတ် <small>Phone Number</small><input name="phoneNo" type="text" inputMode="tel" autoComplete="tel" maxLength={30} required placeholder="09xxxxxxxxx" /></label>

          <div className="location-grid">
            <SearchSelect id="city-code" label="ပြည်နယ် / တိုင်း (City / State / Region)" placeholder="ပြည်နယ် / တိုင်း ရွေးပါ" searchPlaceholder="Myanmar သို့မဟုတ် English ဖြင့် ရှာပါ" options={cities} value={cityCode} onChange={(value) => { setCityCode(value); setTownshipCode(""); setErrors((current) => ({ ...current, location: undefined })); }} />
            <SearchSelect id="township-code" label="မြို့နယ် (Township)" placeholder="မြို့နယ် ရွေးပါ" searchPlaceholder="မြို့နယ်အမည် ရှာပါ" options={townships} value={townshipCode} disabled={!cityCode} onChange={(value) => { setTownshipCode(value); setErrors((current) => ({ ...current, location: undefined })); }} />
          </div>
          <FieldError text={errors.location} />
        </section>

        <section className="card form-section">
          <div className="section-heading"><span>2</span><div><h2>ဆုငွေလက်ခံရန်</h2><p>Payment Information</p></div></div>
          <div className="two-columns">
            <label>Wallet အမျိုးအစား<select name="walletType" required defaultValue=""><option value="" disabled>ရွေးပါ</option>{WALLET_TYPES.map((item) => <option key={item}>{item}</option>)}</select></label>
            <label>Wallet ဖုန်းနံပါတ်<input name="walletAccountNo" inputMode="tel" maxLength={50} required placeholder="09xxxxxxxxx" /></label>
          </div>
        </section>

        <section className="card form-section">
          <div className="section-heading"><span>3</span><div><h2>Brand ရွေးချယ်ရန်</h2><p>Brand Selection</p></div></div>
          <fieldset className="brand-fieldset" aria-describedby="brand-error">
            <legend><strong>Which brand on the display caught your eye today?</strong><span>စင်ပေါ်တွင် သင့်ကို စိတ်ဝင်စားမှု အပေးနိုင်ဆုံး အမှတ်တံဆိပ်</span></legend>
            <div className="brand-grid">
              {BRAND_OPTIONS.map((brand) => {
                const checked = selectedBrands.includes(brand.code);
                return <label className={`brand-card${checked ? " selected" : ""}`} key={brand.code}><input type="checkbox" name="brandCodes" value={brand.code} checked={checked} onChange={() => toggleBrand(brand.code)} /><span>{brand.label}</span><span className="brand-check" aria-hidden="true">✓</span></label>;
              })}
            </div>
            <FieldError id="brand-error" text={errors.brands} />
          </fieldset>
        </section>

        <section className="card form-section">
          <div className="section-heading"><span>4</span><div><h2>ဓာတ်ပုံတင်ရန်</h2><p>Upload Photo</p></div></div>
          <p className="photo-instruction">DKSH မှကုန်ပစ္စည်းများခင်းကျင်းပြသထားသော အနီရောင်စင် တစ်ခုလုံးပါအောင်ဓာတ်ပုံကို ရှင်းလင်းစွာရိုက်ကူးပြီးတင်ပေးပါ။</p>
          <label className="photo-upload">
            <input name="photo" type="file" accept="image/jpeg,image/webp" capture="environment" required disabled={isSending} onChange={(event) => { const file = event.target.files?.[0]; setPhotoInfo(file ? { name: file.name, size: file.size } : null); }} />
            <span className="photo-icon">＋</span>
            <strong>{photoInfo?.name || "ဓာတ်ပုံရိုက်ရန် / ရွေးရန်"}</strong>
            <small>{photoInfo ? `မူရင်းအရွယ်အစား ${formatBytes(photoInfo.size)}` : "JPG သို့မဟုတ် WebP · မူရင်းပုံ အများဆုံး 15 MB"}</small>
            <small>ပေးပို့မီ 1–1.2 MB ခန့်သို့ အလိုအလျောက် ပြင်ဆင်ပါမည်။</small>
          </label>
          {isSending && submitState.message === "ပုံကို ပြင်ဆင်နေပါသည်…" && <p className="photo-progress" role="status">ပုံကို ပြင်ဆင်နေပါသည်…</p>}
        </section>

        <section className="card consent-card">
          <button ref={termsButtonRef} className="terms-button" type="button" onClick={() => setTermsOpen(true)}>Terms &amp; Conditions</button>
          <label className="consent-row">
            <input type="checkbox" checked={consentAccepted} onChange={(event) => { setConsentAccepted(event.target.checked); setErrors((current) => ({ ...current, consent: undefined })); }} />
            <span>စည်းမျဉ်း စည်းကမ်းချက်များကို ဖတ်ရှုနားလည်ပြီး သဘောတူပါသည်။</span>
          </label>
          <FieldError text={errors.consent} />
        </section>

        {turnstileSiteKey && <div className="turnstile-wrap"><div ref={turnstileRef}></div></div>}
        {submitState.kind === "error" && <div className="notice error-notice" role="alert">{submitState.message}</div>}
        <div className="submit-area"><button className="submit-button" type="submit" disabled={formDisabled || isSending}>{isSending ? submitState.message : "ကံစမ်းမဲစာရင်း ပေးပို့မည်"}</button></div>
        <p className="privacy-note">သင့်အချက်အလက်များကို ကံစမ်းမဲစီမံခန့်ခွဲခြင်းနှင့် ကံထူးရှင်ဆက်သွယ်ခြင်းအတွက် အသုံးပြုပါမည်။</p>
      </form>

      <TermsModal open={termsOpen} onClose={closeTerms} triggerRef={termsButtonRef} />
    </main>
  );
}
